/* eslint-disable react-hooks/set-state-in-effect */
/**
 * CartePage — vue globale de tous les dresseurs sur la carte.
 * Utilise RegionMap avec un dresseur "pivot" (le premier) et affiche les
 * autres dresseurs en surimpression via `extraTrainers`. Un sélecteur en tête
 * permet de choisir quel dresseur "conduit" la carte (donc quels voisins
 * sont mis en évidence + voyage manuel disponible).
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import RegionMap from "@/components/RegionMap";

export default function CartePage() {
  const [trainers, setTrainers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const [t, r, me] = await Promise.all([
      api.get("/trainers"),
      api.get("/ref"),
      api.get("/auth/me"),
    ]);
    setTrainers(t.data || []);
    const unlocked = new Set(me.data?.unlocked_regions || ["kanto"]);
    setRegions((r.data?.regions || []).map((rg) => ({ ...rg, unlocked: unlocked.has(rg.id) })));
    setActiveId((prev) => prev || (t.data?.[0]?.id ?? null));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!trainers.length) {
    return (
      <div className="panel p-6 text-center" data-testid="carte-empty">
        <div className="overline">Carte</div>
        <h2 className="font-serif-display text-3xl italic">Aucun dresseur à afficher</h2>
        <p className="font-mono text-[11px] text-[#4A4A4A] mt-2">
          Recrute au moins un dresseur pour ouvrir l&apos;atlas.
        </p>
      </div>
    );
  }

  const activeTrainer = trainers.find((t) => t.id === activeId) || trainers[0];
  const extraTrainers = trainers
    .filter((t) => t.id !== activeTrainer.id)
    .map((t) => ({
      id: t.id, name: t.name, sprite_id: t.sprite_id,
      region: t.current_region || "kanto",
      location: t.current_location || "route_1",
      onClick: () => nav(`/dresseurs?open=${t.id}`),
    }));

  return (
    <div className="space-y-4" data-testid="carte-page">
      {/* Header + sélecteur du dresseur "pilote" */}
      <div className="panel-strong p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Atlas</div>
            <h1 className="font-serif-display text-4xl italic leading-none">Carte</h1>
            <p className="font-mono text-[11px] text-[#4A4A4A] mt-1">
              Vue globale de tes {trainers.length} dresseur(s) — clique un sprite pour ouvrir sa fiche.
              Le pilote actif détermine les routes limitrophes proposées.
            </p>
          </div>
          <div className="flex flex-wrap gap-1" data-testid="carte-pilot-picker">
            {trainers.map((t) => (
              <button
                key={t.id}
                data-testid={`carte-pilot-${t.id}`}
                onClick={() => setActiveId(t.id)}
                className={`px-2 py-1 font-mono text-[10px] uppercase border-2 border-[#1E2D24] flex items-center gap-1 ${
                  t.id === activeTrainer.id ? "bg-[#1E2D24] text-[#F2E8D5]" : "bg-[#FCF9F2] hover:bg-[#EAE4D2]"
                }`}
                title={`Piloter avec ${t.name} (${(t.current_region || "kanto").toUpperCase()})`}
              >
                <img
                  src={`https://play.pokemonshowdown.com/sprites/trainers/${t.sprite_id || "youngster"}.png`}
                  alt={t.name}
                  className="pixelated w-4 h-4"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <span>{t.name.split(" ")[0]}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* La carte */}
      <div className="panel-strong p-3">
        <RegionMap
          key={activeTrainer.id}
          trainerId={activeTrainer.id}
          regions={regions}
          extraTrainers={extraTrainers}
          showAllTrainers
          onTravelled={load}
        />
      </div>
    </div>
  );
}
